import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import Style from './panelStyles.module.scss';
import { useMemo } from 'react';
import { useNode } from '@craftjs/core';
import { ALLOWED_EXTENSIONS, AllowedExtensionsType, FormFileInputProps } from '../../types/FileInput';
import { LayoutValidationContext, ValidationEnum } from '../../validationContext/layoutValidation.context';
import { DropdownOption } from 'uikit/inputs/dropdown/appDropdownInput';
import { PanelContainer } from 'src/components/formEditor/toolbox/components/panelContainer';
import { ControlsGroup } from 'src/components/formEditor/toolbox/components/controlsGroup';
import { SettingsInput } from 'src/components/formEditor/toolbox/settingsPanel/settingsInputs/settingsInput';
import { AppDropdownField } from 'src/features/generalForms/uikit/fields/dropdown/dropDownField';
import { AppCheckboxInput } from 'uikit/inputs';
import { useContextSelector } from 'use-context-selector';

export const FileFieldPanel = () => {
  const { t } = useScopedTranslation('GeneralFormBuilder.Panels.FileField');
  const {
    id,
    actions: { setProp },
    props,
  } = useNode((node) => ({
    props: node.data.props as FormFileInputProps,
  }));

  const extensionsOptions: DropdownOption<AllowedExtensionsType>[] = useMemo(
    () =>
      ALLOWED_EXTENSIONS.map((x) => {
        return {
          key: x,
          text: x,
        };
      }),
    [],
  );

  const extensionValue = useMemo(
    () => extensionsOptions.filter((x) => props.fileTypes?.includes(x.key)),
    [extensionsOptions, props.fileTypes],
  );

  const validationError = useContextSelector(LayoutValidationContext, (x) => x.layoutValidationResult[id]);

  const noFileTypesErrorMessage = useMemo(
    () => (validationError?.type === ValidationEnum.NoFileTypes ? validationError?.message : undefined),
    [validationError?.message, validationError?.type],
  );

  return (
    <PanelContainer header={t('Header')} expanded={!!noFileTypesErrorMessage}>
      <ControlsGroup contentClassName={Style.flexContainer} header={t('AddFileButton')}>
        <SettingsInput
          placeholder={t('AddFileButtonPlaceholder')}
          value={props.addFileButtonText}
          onChange={(e) =>
            setProp((p: FormFileInputProps) => {
              p.addFileButtonText = e.target.value;
            }, 500)
          }
        />
      </ControlsGroup>
      <ControlsGroup contentClassName={Style.flexContainer} header={t('AllowedFileTypes')}>
        <AppDropdownField
          isMultiple
          isRequired
          labelProps={{}}
          inputClassName={Style.tagInputStyle}
          placeholder={t('AllowedFileTypesPlaceholder')}
          options={extensionsOptions}
          value={extensionValue}
          onChange={(selected) => {
            setProp((p: FormFileInputProps) => {
              p.fileTypes = selected?.map((x) => x.key);
            }, 500);
          }}
          errorProps={{ errors: noFileTypesErrorMessage, hideBorder: true }}
        />
      </ControlsGroup>
      <ControlsGroup contentClassName={Style.flexContainer}>
        <AppCheckboxInput
          label={t('IsMultiple')}
          checked={props.isMultiple}
          onChange={(e) =>
            setProp((p: FormFileInputProps) => {
              p.isMultiple = e.target.checked || undefined;
            }, 500)
          }
        />
      </ControlsGroup>
      <ControlsGroup
        header={t('MaxFileSize')}
        contentClassName={Style.gridContainer}
        collapsible={true}
        expanded={props.maxFileSizeMb !== undefined}
        onOpen={() => {
          setProp((prop: FormFileInputProps) => {
            prop.maxFileSizeMb = 200;
          }, 500);
        }}
        onClose={() => {
          setProp((prop: FormFileInputProps) => {
            prop.maxFileSizeMb = undefined;
          }, 500);
        }}
      >
        <SettingsInput
          type={'number'}
          defaultValue={props.maxFileSizeMb}
          units={'MB'}
          onChange={(e) =>
            setProp((prop: FormFileInputProps) => {
              prop.maxFileSizeMb = +e.target.value;
            }, 500)
          }
        />
      </ControlsGroup>
    </PanelContainer>
  );
};
